/**
 * Safe expression evaluator for contract data bindings.
 * ──────────────────────────────────────────────────────
 * A small, sandboxed expression language (NO eval/Function) used by contract
 * bindings to derive token/table values from a data `scope`. Supports:
 *   - literals: 123, 'text', "text", true, false, null, [a, b]
 *   - member/index access: a.b.c, a['k'], arr[0]
 *   - arithmetic: + - * / %   (+ also concatenates when either side is a string)
 *   - comparison/logic: == != < <= > >=  && ||  !  and ternary a ? b : c
 *   - functions: round floor ceil abs min max len lower upper trim str num
 *     currency currency2 pct includes coalesce default not startsWith
 *   - iteration special-forms (lazy 2nd arg, exposes `item`):
 *     sumOver(coll, expr)  mapOver(coll, expr)  filterOver(coll, pred)  countOver(coll, pred?)
 */
const TWO_CHAR_OPS = new Set(['==', '!=', '<=', '>=', '&&', '||']);
const ONE_CHAR_OPS = new Set([
    '+',
    '-',
    '*',
    '/',
    '%',
    '<',
    '>',
    '!',
    '?',
    ':',
    '.',
    ',',
    '(',
    ')',
    '[',
    ']',
]);
function tokenize(src) {
    const tokens = [];
    let i = 0;
    while (i < src.length) {
        const ch = src[i];
        if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
            i += 1;
            continue;
        }
        if (ch === "'" || ch === '"') {
            const quote = ch;
            let value = '';
            i += 1;
            while (i < src.length && src[i] !== quote) {
                if (src[i] === '\\' && i + 1 < src.length) {
                    value += src[i + 1];
                    i += 2;
                }
                else {
                    value += src[i];
                    i += 1;
                }
            }
            i += 1; // closing quote
            tokens.push({ type: 'str', value });
            continue;
        }
        if ((ch >= '0' && ch <= '9') || (ch === '.' && /[0-9]/.test(src[i + 1] ?? ''))) {
            let value = '';
            while (i < src.length && /[0-9.]/.test(src[i])) {
                value += src[i];
                i += 1;
            }
            tokens.push({ type: 'num', value });
            continue;
        }
        if (/[A-Za-z_$]/.test(ch)) {
            let value = '';
            while (i < src.length && /[A-Za-z0-9_$]/.test(src[i])) {
                value += src[i];
                i += 1;
            }
            tokens.push({ type: 'id', value });
            continue;
        }
        const two = src.slice(i, i + 2);
        if (TWO_CHAR_OPS.has(two)) {
            tokens.push({ type: 'op', value: two });
            i += 2;
            continue;
        }
        if (ONE_CHAR_OPS.has(ch)) {
            tokens.push({ type: 'op', value: ch });
            i += 1;
            continue;
        }
        throw new Error(`expr: unexpected character '${ch}' at ${i}`);
    }
    tokens.push({ type: 'eof', value: '' });
    return tokens;
}
const BINARY_PRECEDENCE = {
    '||': 1,
    '&&': 2,
    '==': 3,
    '!=': 3,
    '<': 4,
    '<=': 4,
    '>': 4,
    '>=': 4,
    '+': 5,
    '-': 5,
    '*': 6,
    '/': 6,
    '%': 6,
};
class Parser {
    tokens;
    pos = 0;
    constructor(tokens) {
        this.tokens = tokens;
    }
    peek() {
        return this.tokens[this.pos];
    }
    next() {
        return this.tokens[this.pos++];
    }
    expectOp(op) {
        const t = this.next();
        if (t.type !== 'op' || t.value !== op)
            throw new Error(`expr: expected '${op}', got '${t.value}'`);
    }
    parse() {
        const node = this.parseTernary();
        if (this.peek().type !== 'eof')
            throw new Error(`expr: unexpected trailing '${this.peek().value}'`);
        return node;
    }
    parseTernary() {
        const cond = this.parseBinary(0);
        if (this.peek().type === 'op' && this.peek().value === '?') {
            this.next();
            const a = this.parseTernary();
            this.expectOp(':');
            const b = this.parseTernary();
            return { t: 'ternary', cond, a, b };
        }
        return cond;
    }
    parseBinary(minPrec) {
        let left = this.parseUnary();
        for (;;) {
            const t = this.peek();
            if (t.type !== 'op' || !(t.value in BINARY_PRECEDENCE))
                break;
            const prec = BINARY_PRECEDENCE[t.value];
            if (prec < minPrec)
                break;
            this.next();
            const right = this.parseBinary(prec + 1);
            left = { t: 'bin', op: t.value, l: left, r: right };
        }
        return left;
    }
    parseUnary() {
        const t = this.peek();
        if (t.type === 'op' && (t.value === '-' || t.value === '!')) {
            this.next();
            return { t: 'unary', op: t.value, arg: this.parseUnary() };
        }
        return this.parsePostfix();
    }
    parsePostfix() {
        let node = this.parsePrimary();
        for (;;) {
            const t = this.peek();
            if (t.type === 'op' && t.value === '.') {
                this.next();
                const prop = this.next();
                if (prop.type !== 'id')
                    throw new Error(`expr: expected property name after '.'`);
                node = { t: 'member', obj: node, prop: prop.value };
            }
            else if (t.type === 'op' && t.value === '[') {
                this.next();
                const idx = this.parseTernary();
                this.expectOp(']');
                node = { t: 'index', obj: node, idx };
            }
            else {
                break;
            }
        }
        return node;
    }
    parsePrimary() {
        const t = this.next();
        if (t.type === 'num')
            return { t: 'num', v: Number(t.value) };
        if (t.type === 'str')
            return { t: 'str', v: t.value };
        if (t.type === 'id') {
            if (t.value === 'true')
                return { t: 'bool', v: true };
            if (t.value === 'false')
                return { t: 'bool', v: false };
            if (t.value === 'null')
                return { t: 'null' };
            // function call?
            if (this.peek().type === 'op' && this.peek().value === '(') {
                this.next();
                const args = [];
                if (!(this.peek().type === 'op' && this.peek().value === ')')) {
                    args.push(this.parseTernary());
                    while (this.peek().type === 'op' && this.peek().value === ',') {
                        this.next();
                        args.push(this.parseTernary());
                    }
                }
                this.expectOp(')');
                return { t: 'call', callee: t.value, args };
            }
            return { t: 'id', name: t.value };
        }
        if (t.type === 'op' && t.value === '(') {
            const node = this.parseTernary();
            this.expectOp(')');
            return node;
        }
        if (t.type === 'op' && t.value === '[') {
            const items = [];
            if (!(this.peek().type === 'op' && this.peek().value === ']')) {
                items.push(this.parseTernary());
                while (this.peek().type === 'op' && this.peek().value === ',') {
                    this.next();
                    items.push(this.parseTernary());
                }
            }
            this.expectOp(']');
            return { t: 'array', items };
        }
        throw new Error(`expr: unexpected token '${t.value}'`);
    }
}
const PARSE_CACHE = new Map();
function parseExpr(src) {
    let node = PARSE_CACHE.get(src);
    if (!node) {
        node = new Parser(tokenize(src)).parse();
        PARSE_CACHE.set(src, node);
    }
    return node;
}
// ── Evaluator ────────────────────────────────────────────────────────────────────
function truthy(v) {
    if (v === null || v === undefined || v === false)
        return false;
    if (typeof v === 'number')
        return v !== 0 && !Number.isNaN(v);
    if (typeof v === 'string')
        return v.trim().length > 0;
    if (Array.isArray(v))
        return v.length > 0;
    return true;
}
/**
 * Loose equality: strict by default, but numeric when either side is a number
 * (so `3 == '3'` is true while `'dev' == 'consulting'` is false - two distinct
 * strings must not both coerce to 0).
 */
function looseEquals(l, r) {
    if (l === r)
        return true;
    if (typeof l === 'number' || typeof r === 'number')
        return toNum(l) === toNum(r);
    return false;
}
function toNum(v) {
    if (typeof v === 'number')
        return Number.isFinite(v) ? v : 0;
    if (typeof v === 'string') {
        const n = parseFloat(v.replace(/,/g, ''));
        return Number.isFinite(n) ? n : 0;
    }
    if (typeof v === 'boolean')
        return v ? 1 : 0;
    return 0;
}
function formatCurrency(v) {
    const rounded = Math.round(toNum(v) * 100) / 100;
    const hasFraction = !Number.isInteger(rounded);
    return rounded.toLocaleString('en-US', {
        minimumFractionDigits: hasFraction ? 2 : 0,
        maximumFractionDigits: 2,
    });
}
// ── Date helpers (UTC, to avoid TZ drift) ────────────────────────────────────────
// Internal date representation is an ISO "YYYY-MM-DD" string so date functions chain
// (e.g. formatDate(endOfMonth(addMonths(goLive, 2)))). An unparseable input yields ''
// so callers can `coalesce` a fallback phrase.
function parseDateUTC(v) {
    if (v instanceof Date)
        return Number.isNaN(v.getTime()) ? null : v;
    const s = String(v ?? '').trim();
    if (!s)
        return null;
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso)
        return new Date(Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])));
    const t = Date.parse(s);
    return Number.isNaN(t) ? null : new Date(t);
}
function toISODate(d) {
    return d.toISOString().slice(0, 10);
}
function addMonthsUTC(d, n) {
    const day = d.getUTCDate();
    const target = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1));
    const lastDay = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
    target.setUTCDate(Math.min(day, lastDay)); // clamp (e.g. Jan 31 +1mo -> Feb 28/29)
    return target;
}
function endOfMonthUTC(d) {
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0));
}
function formatDisplayDate(d) {
    return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC',
    });
}
/** Offset a date by `n` days/weeks/months; returns ISO "YYYY-MM-DD" or '' if unparseable. */
function dateOffset(v, n, unit) {
    const d = parseDateUTC(v);
    if (!d)
        return '';
    const out = new Date(d.getTime());
    if (unit === 'day')
        out.setUTCDate(out.getUTCDate() + n);
    else if (unit === 'week')
        out.setUTCDate(out.getUTCDate() + n * 7);
    else
        return toISODate(addMonthsUTC(out, n));
    return toISODate(out);
}
function pickDate(args, pick) {
    const dates = args.map(parseDateUTC).filter((d) => d !== null);
    if (dates.length === 0)
        return '';
    const chosen = dates.reduce((acc, d) => pick === 'min' ? (d.getTime() < acc.getTime() ? d : acc) : d.getTime() > acc.getTime() ? d : acc);
    return toISODate(chosen);
}
/** Roll a date forward to the next business day (Mon-Fri) if it falls on Saturday or Sunday. */
function nextBusinessDayUTC(v) {
    const d = parseDateUTC(v);
    if (!d)
        return '';
    const out = new Date(d.getTime());
    const day = out.getUTCDay();
    if (day === 6)
        out.setUTCDate(out.getUTCDate() + 2);
    else if (day === 0)
        out.setUTCDate(out.getUTCDate() + 1);
    return toISODate(out);
}
const FUNCTIONS = {
    // Date math (see helpers above). Inputs accept ISO or display strings.
    addDays: (a) => dateOffset(a[0], toNum(a[1]), 'day'),
    addWeeks: (a) => dateOffset(a[0], toNum(a[1]), 'week'),
    addMonths: (a) => dateOffset(a[0], toNum(a[1]), 'month'),
    nextBusinessDay: (a) => nextBusinessDayUTC(a[0]),
    endOfMonth: (a) => {
        const d = parseDateUTC(a[0]);
        return d ? toISODate(endOfMonthUTC(d)) : '';
    },
    minDate: (a) => pickDate(a, 'min'),
    maxDate: (a) => pickDate(a, 'max'),
    // Display-format a date; passes non-dates through unchanged (so a fallback phrase
    // like "two months after the final go-live date" survives `formatDate(coalesce(...))`).
    formatDate: (a) => {
        const d = parseDateUTC(a[0]);
        return d ? formatDisplayDate(d) : String(a[0] ?? '');
    },
    // Month-and-year only (e.g. "June 2026") for contract-term style ranges where the
    // day is noise. Same non-date passthrough as formatDate.
    formatMonthYear: (a) => {
        const d = parseDateUTC(a[0]);
        return d
            ? d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', timeZone: 'UTC' })
            : String(a[0] ?? '');
    },
    // `pctOf(base, percent)` = base * percent / 100 (the numeric value, e.g. "30% of fees").
    pctOf: (a) => (toNum(a[1]) / 100) * toNum(a[0]),
    round: (a) => Math.round(toNum(a[0])),
    floor: (a) => Math.floor(toNum(a[0])),
    ceil: (a) => Math.ceil(toNum(a[0])),
    abs: (a) => Math.abs(toNum(a[0])),
    min: (a) => Math.min(...a.map(toNum)),
    max: (a) => Math.max(...a.map(toNum)),
    num: (a) => toNum(a[0]),
    str: (a) => (a[0] === null || a[0] === undefined ? '' : String(a[0])),
    len: (a) => Array.isArray(a[0]) || typeof a[0] === 'string' ? a[0].length : 0,
    lower: (a) => String(a[0] ?? '').toLowerCase(),
    upper: (a) => String(a[0] ?? '').toUpperCase(),
    trim: (a) => String(a[0] ?? '').trim(),
    currency: (a) => formatCurrency(a[0]),
    // Contract-style money: always two decimals ("16,000.00"), unlike `currency`
    // which drops ".00" on whole amounts.
    currency2: (a) => {
        const rounded = Math.round(toNum(a[0]) * 100) / 100;
        return rounded.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    },
    pct: (a) => `${toNum(a[0])}%`,
    not: (a) => !truthy(a[0]),
    startsWith: (a) => String(a[0] ?? '').startsWith(String(a[1] ?? '')),
    includes: (a) => {
        if (Array.isArray(a[0]))
            return a[0].includes(a[1]);
        return String(a[0] ?? '')
            .toLowerCase()
            .includes(String(a[1] ?? '').toLowerCase());
    },
    coalesce: (a) => a.find((x) => truthy(x)) ?? '',
    default: (a) => (truthy(a[0]) ? a[0] : a[1]),
};
const SPECIAL_FORMS = new Set(['sumOver', 'mapOver', 'filterOver', 'countOver']);
function asArray(v) {
    return Array.isArray(v) ? v : [];
}
function evalNode(node, scope) {
    switch (node.t) {
        case 'num':
            return node.v;
        case 'str':
            return node.v;
        case 'bool':
            return node.v;
        case 'null':
            return null;
        case 'id':
            return scope[node.name];
        case 'array':
            return node.items.map((n) => evalNode(n, scope));
        case 'unary': {
            const v = evalNode(node.arg, scope);
            return node.op === '-' ? -toNum(v) : !truthy(v);
        }
        case 'ternary':
            return truthy(evalNode(node.cond, scope)) ? evalNode(node.a, scope) : evalNode(node.b, scope);
        case 'member': {
            const obj = evalNode(node.obj, scope);
            if (obj === null || obj === undefined)
                return undefined;
            return obj[node.prop];
        }
        case 'index': {
            const obj = evalNode(node.obj, scope);
            const idx = evalNode(node.idx, scope);
            if (obj === null || obj === undefined)
                return undefined;
            return obj[idx];
        }
        case 'bin':
            return evalBinary(node, scope);
        case 'call':
            return evalCall(node, scope);
    }
}
function evalBinary(node, scope) {
    if (node.op === '&&')
        return truthy(evalNode(node.l, scope)) ? evalNode(node.r, scope) : false;
    if (node.op === '||') {
        const l = evalNode(node.l, scope);
        return truthy(l) ? l : evalNode(node.r, scope);
    }
    const l = evalNode(node.l, scope);
    const r = evalNode(node.r, scope);
    switch (node.op) {
        case '+':
            if (typeof l === 'string' || typeof r === 'string')
                return `${l ?? ''}${r ?? ''}`;
            return toNum(l) + toNum(r);
        case '-':
            return toNum(l) - toNum(r);
        case '*':
            return toNum(l) * toNum(r);
        case '/':
            return toNum(r) === 0 ? 0 : toNum(l) / toNum(r);
        case '%':
            return toNum(r) === 0 ? 0 : toNum(l) % toNum(r);
        case '==':
            return looseEquals(l, r);
        case '!=':
            return !looseEquals(l, r);
        case '<':
            return toNum(l) < toNum(r);
        case '<=':
            return toNum(l) <= toNum(r);
        case '>':
            return toNum(l) > toNum(r);
        case '>=':
            return toNum(l) >= toNum(r);
        default:
            throw new Error(`expr: unknown operator '${node.op}'`);
    }
}
function evalCall(node, scope) {
    if (SPECIAL_FORMS.has(node.callee)) {
        const coll = asArray(evalNode(node.args[0], scope));
        const bodyNode = node.args[1];
        const run = (item) => (bodyNode ? evalNode(bodyNode, { ...scope, item }) : item);
        switch (node.callee) {
            case 'sumOver':
                return coll.reduce((sum, item) => sum + toNum(run(item)), 0);
            case 'mapOver':
                return coll.map((item) => run(item));
            case 'filterOver':
                return coll.filter((item) => truthy(run(item)));
            case 'countOver':
                return bodyNode ? coll.filter((item) => truthy(run(item))).length : coll.length;
        }
    }
    const fn = FUNCTIONS[node.callee];
    if (!fn)
        throw new Error(`expr: unknown function '${node.callee}'`);
    return fn(node.args.map((a) => evalNode(a, scope)));
}
/** Evaluate an expression string against a scope. Returns the raw value. */
export function evaluateExpression(src, scope) {
    return evalNode(parseExpr(src), scope);
}
/** Evaluate and coerce to a display string (numbers/booleans → string, null → ''). */
export function evaluateToString(src, scope) {
    const v = evaluateExpression(src, scope);
    if (v === null || v === undefined)
        return '';
    return String(v);
}
/** Parse-only syntax validation: throws on malformed expressions, no evaluation. */
export function validateExpressionSyntax(src) {
    parseExpr(src);
}
//# sourceMappingURL=expr.js.map